import type { RewardMerchant_Category } from '@/api/generated';
import type { ResolveRewardMerchantLocationLinkResponse, RewardMerchant } from '@/api/types';
import {
  SAME_PLACE_METRES,
  distanceMetres,
  fromLocalInput,
  merchantCategoryOf,
  parseDegrees,
  toLocalInput,
  type Coordinates,
} from '@/lib/rewards';

// The partner form's catalogue sections (LIM-4607): category, links,
// locations and active windows, as the operator edits them. All four are
// catalogue paths, saved in place on the catalogue update with no reason.

/** The update-mask paths these sections write. */
export type CataloguePath = 'category' | 'links' | 'locations' | 'active_windows';

/** The most of each the form offers, and the longest values admin_api takes (its buf.validate rules). */
export const MAX_LINKS = 10;
export const MAX_LOCATIONS = 100;
export const LINK_LABEL_MAX = 60;
export const LINK_URI_MAX = 2048;
export const LOCATION_LABEL_MAX = 120;
export const ADDRESS_MAX = 300;

/** The label of a partner's Google Maps link: a preset in Links, and the label the first location's link joins Links under. */
export const GOOGLE_MAPS_LINK_LABEL = 'Google Maps';

export interface LinkRow {
  readonly key: string;
  readonly label: string;
  readonly uri: string;
}

/**
 * Where a location row's Google Maps link stands with the resolver. Each
 * state past `unsent` names the link it is about, so an answer for a link the
 * operator has since replaced is recognised and dropped.
 */
export type LinkResolution =
  | { readonly state: 'unsent' }
  | { readonly state: 'resolving'; readonly uri: string }
  | { readonly state: 'resolved'; readonly uri: string }
  | { readonly state: 'failed'; readonly uri: string; readonly message: string };

/** A row entered by its Google Maps link: `uri` is the field as typed, and the coordinates come from the resolver. */
export interface LinkEntry {
  readonly mode: 'link';
  readonly uri: string;
  readonly resolution: LinkResolution;
}

/** A row entered by its address and coordinates, typed by hand. */
export interface AddressEntry {
  readonly mode: 'address';
}

/**
 * How the operator is entering a location (LIM-4621). Only the form knows it:
 * the location saved is the same label, coordinates and address either way,
 * and the link itself is not stored.
 */
export type LocationEntry = LinkEntry | AddressEntry;

/** A location as typed: the coordinates stay text until the form is saved. */
export interface LocationRow {
  readonly key: string;
  readonly label: string;
  readonly latitude: string;
  readonly longitude: string;
  readonly address: string;
  readonly entry: LocationEntry;
}

/** The row "Add location" appends: link mode, nothing pasted yet. */
export function newLinkLocation(key: string): LocationRow {
  return {
    key,
    label: '',
    latitude: '',
    longitude: '',
    address: '',
    entry: { mode: 'link', uri: '', resolution: { state: 'unsent' } },
  };
}

/** A window as its two `datetime-local` inputs hold it, in the operator's own timezone. */
export interface WindowRow {
  readonly key: string;
  readonly start: string;
  readonly end: string;
}

export interface CatalogueForm {
  /** Empty until chosen: a partner added before categories has none. */
  readonly category: RewardMerchant_Category | '';
  readonly links: readonly LinkRow[];
  readonly locations: readonly LocationRow[];
  readonly windows: readonly WindowRow[];
}

/** A key for a row the operator adds; rows read from the server are keyed by position. */
export function newRowKey(): string {
  return crypto.randomUUID();
}

export function toCatalogueForm(merchant: RewardMerchant | undefined): CatalogueForm {
  return {
    category: merchantCategoryOf(merchant?.category) ?? '',
    links: (merchant?.links ?? []).map((link, i) => ({ key: `link-${i}`, label: link.label, uri: link.uri })),
    // Proto3 JSON: an absent coordinate is 0, a real latitude or longitude
    // (the server refuses only a location at exactly 0, 0).
    locations: (merchant?.locations ?? []).map((location, i) => ({
      key: `location-${i}`,
      label: location.label,
      latitude: String(location.latitude ?? 0),
      longitude: String(location.longitude ?? 0),
      address: location.address ?? '',
      // The link a location came from is not stored, so a saved one is shown by its coordinates.
      entry: { mode: 'address' },
    })),
    windows: (merchant?.activeWindows ?? []).map((span, i) => ({
      key: `window-${i}`,
      start: toLocalInput(span.startTime),
      end: toLocalInput(span.endTime),
    })),
  };
}

export interface LinkErrors {
  readonly label?: string;
  readonly uri?: string;
}

export interface LocationErrors {
  /** A link-mode row's Google Maps link: missing, still being read, or unreadable. */
  readonly uri?: string;
  readonly label?: string;
  readonly latitude?: string;
  readonly longitude?: string;
  readonly address?: string;
}

export interface WindowErrors {
  readonly start?: string;
  readonly end?: string;
}

/** What is wrong with each row, keyed by the row's key; a row with nothing wrong has no entry. */
export interface CatalogueErrors {
  readonly category?: string;
  readonly links: Readonly<Record<string, LinkErrors>>;
  readonly locations: Readonly<Record<string, LocationErrors>>;
  readonly windows: Readonly<Record<string, WindowErrors>>;
  /** The first problem, for the banner by Save; null when the sections can be saved. */
  readonly first: string | null;
}

/** What the sections show before the first save attempt: nothing, so an empty new form is not all red. */
export const NO_CATALOGUE_ERRORS: CatalogueErrors = { links: {}, locations: {}, windows: {}, first: null };

/** Whether a link is one the server takes: a full https:// address. */
function isHttpsLink(text: string): boolean {
  if (!text.startsWith('https://')) return false;
  try {
    return new URL(text).hostname !== '';
  } catch {
    return false;
  }
}

function linkErrors(row: LinkRow): LinkErrors {
  const label = row.label.trim();
  const uri = row.uri.trim();
  return {
    label:
      label === '' ? 'Give the link a label.' : label.length > LINK_LABEL_MAX ? `Keep the label to ${LINK_LABEL_MAX} characters.` : undefined,
    uri:
      uri === ''
        ? 'Add the link.'
        : !isHttpsLink(uri)
          ? 'A link must be a full address starting with https://.'
          : uri.length > LINK_URI_MAX
            ? `Keep the link to ${LINK_URI_MAX} characters.`
            : undefined,
  };
}

/**
 * What keeps a link-mode row from saving. Its coordinates must have come from
 * the link the field shows now, or the pin saved would be another place's.
 */
function linkError({ uri, resolution }: LinkEntry): string | undefined {
  const link = uri.trim();
  if (link === '') return "Paste the place's Google Maps link.";
  if (resolution.state === 'resolving') return 'Wait for the place to be read from this link.';
  if (resolution.state === 'failed' && resolution.uri === link) {
    return 'This link has no place to pin. Use another, or enter the address and coordinates.';
  }
  if (resolution.state !== 'resolved' || resolution.uri !== link) return 'This link has not been read yet. Leave the field to read it.';
  return undefined;
}

function locationErrors(row: LocationRow): LocationErrors {
  const label = row.label.trim();
  if (row.entry.mode === 'link') {
    const uri = linkError(row.entry);
    // Until the link is read there is no place to name or pin, so the link is all that is wrong.
    if (uri !== undefined) return { uri };
  }
  const latitude = parseDegrees(row.latitude, 'latitude');
  const longitude = parseDegrees(row.longitude, 'longitude');
  return {
    label:
      label === ''
        ? 'Name the place, such as its neighbourhood.'
        : label.length > LOCATION_LABEL_MAX
          ? `Keep the name to ${LOCATION_LABEL_MAX} characters.`
          : undefined,
    latitude:
      latitude === null
        ? 'Latitude is a number from -90 to 90.'
        : latitude === 0 && longitude === 0
          ? '0, 0 is where a location without coordinates lands. Check them.'
          : undefined,
    longitude: longitude === null ? 'Longitude is a number from -180 to 180.' : undefined,
    address: row.address.trim().length > ADDRESS_MAX ? `Keep the address to ${ADDRESS_MAX} characters.` : undefined,
  };
}

/** A `datetime-local` value as epoch milliseconds in the operator's timezone, or null when it is empty or not a moment. */
function localMoment(value: string): number | null {
  if (value === '') return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function windowErrors(row: WindowRow): WindowErrors {
  const start = localMoment(row.start);
  const end = localMoment(row.end);
  return {
    start: start === null ? 'Set when the window opens.' : undefined,
    end:
      end === null
        ? 'Set when the window closes.'
        : start !== null && end <= start
          ? 'The window must close after it opens.'
          : undefined,
  };
}

/** Keeps only the rows with something wrong, so an empty record means the section is fine. */
function withProblems<R extends { readonly key: string }, E extends object>(
  rows: readonly R[],
  check: (row: R) => E,
): Record<string, E> {
  const out: Record<string, E> = {};
  for (const row of rows) {
    const errors = check(row);
    if (Object.values(errors).some((message) => message !== undefined)) out[row.key] = errors;
  }
  return out;
}

function firstMessage(record: Readonly<Record<string, object>>): string | null {
  for (const errors of Object.values(record)) {
    const message = Object.values(errors).find((m): m is string => typeof m === 'string');
    if (message) return message;
  }
  return null;
}

/**
 * Everything wrong with the sections, row by row, and the first of it for the
 * banner. A new partner needs a category. One added before categories may be
 * saved without one: the services refuse a `category` path until they carry
 * the catalogue fields, and a rate change must not wait on that.
 */
export function validateCatalogue(form: CatalogueForm, requireCategory: boolean): CatalogueErrors {
  const category = requireCategory && form.category === '' ? "Pick the partner's category." : undefined;
  const links = withProblems(form.links, linkErrors);
  const locations = withProblems(form.locations, locationErrors);
  const windows = withProblems(form.windows, windowErrors);
  const tooMany =
    form.links.length > MAX_LINKS
      ? `A partner can have at most ${MAX_LINKS} links.`
      : form.locations.length > MAX_LOCATIONS
        ? `A partner can have at most ${MAX_LOCATIONS} locations.`
        : null;
  return {
    category,
    links,
    locations,
    windows,
    first: category ?? tooMany ?? firstMessage(links) ?? firstMessage(locations) ?? firstMessage(windows),
  };
}

/** A coordinate the form has already validated. */
function degrees(text: string, axis: 'latitude' | 'longitude'): number {
  const value = parseDegrees(text, axis);
  if (value === null) throw new Error(`"${text}" is not a ${axis}; validate the form before saving it`);
  return value;
}

/** A window end the form has already validated, as the RFC 3339 UTC moment it names (LIM-1402 rule 1). */
function utc(value: string): string {
  const iso = fromLocalInput(value);
  if (iso === undefined) throw new Error('an empty window time; validate the form before saving it');
  return iso;
}

/**
 * The sections as the merchant carries them. Call it only once
 * validateCatalogue finds nothing: an unreadable coordinate or window throws
 * rather than being sent as a made-up value. A partner with no category
 * chosen is sent with none, and its mask never names `category`.
 */
export function catalogueFields(form: CatalogueForm): Pick<RewardMerchant, 'category' | 'links' | 'locations' | 'activeWindows'> {
  return {
    ...(form.category === '' ? {} : { category: form.category }),
    links: form.links.map((row) => ({ label: row.label.trim(), uri: row.uri.trim() })),
    locations: form.locations.map((row) => ({
      label: row.label.trim(),
      latitude: degrees(row.latitude, 'latitude'),
      longitude: degrees(row.longitude, 'longitude'),
      address: row.address.trim(),
    })),
    activeWindows: form.windows.map((row) => ({ startTime: utc(row.start), endTime: utc(row.end) })),
  };
}

/** A section as compared for a change: the rows without their keys, trimmed as they would be sent. */
function comparable(form: CatalogueForm) {
  return {
    category: form.category,
    links: JSON.stringify(form.links.map((row) => [row.label.trim(), row.uri.trim()])),
    locations: JSON.stringify(
      form.locations.map((row) => [
        row.label.trim(),
        parseDegrees(row.latitude, 'latitude') ?? row.latitude,
        parseDegrees(row.longitude, 'longitude') ?? row.longitude,
        row.address.trim(),
      ]),
    ),
    windows: JSON.stringify(form.windows.map((row) => [row.start, row.end])),
  };
}

/** The mask paths whose section differs from the partner as it was read. */
export function changedCataloguePaths(form: CatalogueForm, original: CatalogueForm): CataloguePath[] {
  const now = comparable(form);
  const was = comparable(original);
  const paths: CataloguePath[] = [];
  if (now.category !== was.category) paths.push('category');
  if (now.links !== was.links) paths.push('links');
  if (now.locations !== was.locations) paths.push('locations');
  if (now.windows !== was.windows) paths.push('active_windows');
  return paths;
}

/** The place a Maps link names, ready to become a location row. */
export interface ResolvedPlace {
  readonly coordinates: Coordinates;
  readonly label: string;
}

/**
 * The place in a resolver response, labelled with its name or, when the link
 * names none, `fallbackLabel` (the link's own label). Proto3 JSON leaves a
 * zero coordinate out; a response at exactly 0, 0 carries no place (the
 * server refuses such links), so it throws rather than pinning one there.
 */
export function resolvedPlace(response: ResolveRewardMerchantLocationLinkResponse, fallbackLabel: string): ResolvedPlace {
  const latitude = response.latitude ?? 0;
  const longitude = response.longitude ?? 0;
  if (latitude === 0 && longitude === 0) throw new Error('The link resolved to no coordinates.');
  const name = response.displayName?.trim() ?? '';
  return { coordinates: { latitude, longitude }, label: name === '' ? fallbackLabel.trim() : name };
}

/** What adding a resolved place does to the locations. */
export type ResolvedLocationOutcome =
  | { readonly kind: 'added'; readonly row: LocationRow }
  | { readonly kind: 'nearby'; readonly existing: LocationRow }
  | { readonly kind: 'full' };

/**
 * Where a resolved place goes: a new row keyed `key`, unless a location
 * already listed is within SAME_PLACE_METRES of it (the same place, likely
 * typed by hand), or the locations are full. A row whose coordinates do not
 * read yet is not compared.
 */
export function placeResolvedLocation(
  locations: readonly LocationRow[],
  place: ResolvedPlace,
  key: string,
): ResolvedLocationOutcome {
  for (const row of locations) {
    const latitude = parseDegrees(row.latitude, 'latitude');
    const longitude = parseDegrees(row.longitude, 'longitude');
    if (latitude === null || longitude === null) continue;
    if (distanceMetres({ latitude, longitude }, place.coordinates) <= SAME_PLACE_METRES) {
      return { kind: 'nearby', existing: row };
    }
  }
  if (locations.length >= MAX_LOCATIONS) return { kind: 'full' };
  return {
    kind: 'added',
    row: {
      key,
      label: place.label,
      latitude: String(place.coordinates.latitude),
      longitude: String(place.coordinates.longitude),
      address: '',
      // Its link is the link row's, shown there, so the location is listed by its coordinates.
      entry: { mode: 'address' },
    },
  };
}
